import { useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { LifeBuoy, Phone, Plus, Search, Server, UserPlus, Users, Volume2 } from 'lucide-react';
import { idTimestamp, type Channel, type MessagePreview, type PublicUser } from '@spawnwire/shared';
import { Avatar, Badge, clsx, SystemTag } from '../../components/ui';
import { useNow } from '../../lib/hooks';
import { decodeMentions } from '../../lib/mentionText';
import { NameText } from '../../lib/nameFonts';
import { shortAgo } from '../../lib/time';
import { dmPartner, isUnread, mentionCount, privateChannelName, useStore, type AppState } from '../../store/store';
import { ui } from '../../store/ui';

/** The phone layout's "Messages" heading with search, Add Friends and a new group button. */
export function MobileMessagesHeader() {
  const navigate = useNavigate();
  return (
    <div className="shrink-0 px-4 pb-2 pt-4">
      <h1 className="text-2xl font-bold tracking-tight text-fg">Messages</h1>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          aria-label="Find or start a conversation"
          onClick={() => ui.open({ kind: 'quickSwitcher' })}
          className="flex size-11 shrink-0 items-center justify-center rounded-2xl border border-line bg-rail text-fg active:bg-hover"
        >
          <Search className="size-5" aria-hidden />
        </button>
        <button
          type="button"
          onClick={() => {
            navigate('/app/@me', { state: { friendsTab: 'add' } });
            ui.setMobilePane('main');
          }}
          className="flex h-11 min-w-0 flex-1 items-center justify-center gap-2 rounded-2xl border border-line bg-rail text-[15px] font-semibold text-fg active:bg-hover"
        >
          <UserPlus className="size-5" aria-hidden /> Add Friends
        </button>
        <button
          type="button"
          aria-label="New group conversation"
          onClick={() => ui.open({ kind: 'newGroup' })}
          className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-accent text-accent-fg active:bg-accent-hover"
        >
          <Plus className="size-6" aria-hidden />
        </button>
      </div>
    </div>
  );
}

function Chip({ active, icon: Icon, label, onClick, children }: { active: boolean; icon: typeof Users; label: string; onClick: () => void; children?: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={clsx(
        'flex h-9 shrink-0 items-center gap-2 rounded-full border px-3.5 text-sm font-medium',
        active ? 'border-transparent bg-active text-fg' : 'border-line bg-rail text-muted active:bg-hover',
      )}
    >
      <Icon className="size-4" aria-hidden />
      {label}
      {children}
    </button>
  );
}

/** Friends, Game Servers and Support as a row of chips under the heading. */
export function MobileShortcuts({
  onFriends,
  onServers,
  onSupport,
  supportReplies,
  go,
}: {
  onFriends: boolean;
  onServers: boolean;
  onSupport: boolean;
  supportReplies: number;
  go: (to: string) => void;
}) {
  const pending = useStore((s) => Object.values(s.relationships).filter((r) => r.type === 'incoming').length);
  return (
    <div className="flex shrink-0 gap-2 overflow-x-auto px-4 pb-1 pt-1 [scrollbar-width:none]">
      <Chip active={onFriends} icon={Users} label="Friends" onClick={() => go('/app/@me')}>
        <Badge count={pending} />
      </Chip>
      <Chip active={onServers} icon={Server} label="Game Servers" onClick={() => go('/app/servers')} />
      <Chip active={onSupport} icon={LifeBuoy} label="Support" onClick={() => go('/app/support')}>
        {supportReplies > 0 && (
          <span
            className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold leading-none text-white"
            aria-label={`${supportReplies} new support ${supportReplies === 1 ? 'reply' : 'replies'}`}
          >
            {supportReplies}
          </span>
        )}
      </Chip>
    </div>
  );
}

interface ActiveCall {
  channel: Channel;
  people: PublicUser[];
  title: string;
  where: string;
  path: string;
}

/** Calls happening now that you can see: your conversations, and voice channels a friend is in. */
function useActiveCalls(): ActiveCall[] {
  const voiceStates = useStore((s) => s.voiceStates);
  const channels = useStore((s) => s.channels);
  const spaces = useStore((s) => s.spaces);
  const users = useStore((s) => s.users);
  const relationships = useStore((s) => s.relationships);
  const meId = useStore((s) => s.me?.id);
  return useMemo(() => {
    const out: ActiveCall[] = [];
    for (const [channelId, states] of Object.entries(voiceStates)) {
      const channel = channels[channelId];
      const ids = Object.keys(states);
      if (!channel || ids.length === 0) continue;
      const isPrivate = !channel.spaceId;
      if (!isPrivate && !ids.some((id) => relationships[id]?.type === 'friend' || id === meId)) continue;
      const people = ids.map((id) => users[id]).filter((u): u is PublicUser => !!u);
      if (people.length === 0) continue;
      const names = people.map((p) => p.displayName);
      const title = names.length <= 2 ? names.join(' and ') : `${names.slice(0, 2).join(', ')} and ${names.length - 2} other${names.length === 3 ? '' : 's'}`;
      const state = useStore.getState();
      out.push({
        channel,
        people,
        title,
        where: isPrivate ? privateChannelName(state, channel) : `${channel.name} · ${spaces[channel.spaceId!]?.name ?? ''}`,
        path: isPrivate ? `/app/@me/${channel.id}` : `/app/${channel.spaceId}/${channel.id}`,
      });
    }
    return out;
  }, [voiceStates, channels, spaces, users, relationships, meId]);
}

/** Cards for the calls going on right now, side by side, like a "happening now" strip. */
export function MobileActiveCalls({ go }: { go: (to: string) => void }) {
  const calls = useActiveCalls();
  if (calls.length === 0) return null;
  return (
    <section aria-label="Calls happening now" className="flex shrink-0 snap-x gap-3 overflow-x-auto px-4 py-2 [scrollbar-width:none]">
      {calls.map((c) => (
        <button
          key={c.channel.id}
          type="button"
          onClick={() => go(c.path)}
          className="flex w-[86%] max-w-80 shrink-0 snap-start items-center gap-3 rounded-2xl border border-line bg-rail p-3 text-left active:bg-hover"
        >
          <span className="relative h-12 w-14 shrink-0">
            {c.people.slice(0, 3).map((p, i) => (
              <span key={p.id} className={clsx('absolute rounded-full ring-2 ring-rail', i === 0 ? 'left-0 top-0' : i === 1 ? 'right-0 top-1' : 'bottom-0 left-2')}>
                <Avatar user={p} size={i === 0 ? 30 : 24} />
              </span>
            ))}
          </span>
          <span className="min-w-0 flex-1">
            <span className="line-clamp-2 text-[15px] font-semibold leading-snug text-fg">{c.title}</span>
            <span className="block truncate text-sm text-muted">{c.where}</span>
          </span>
          <Volume2 className="size-5 shrink-0 self-start text-success" aria-label="In voice" />
        </button>
      ))}
    </section>
  );
}

const SYSTEM_TEXT: Partial<Record<MessagePreview['type'], string>> = {
  call: 'Started a call',
  call_timeout: 'Missed call',
  recipient_add: 'Added someone to the group',
  recipient_remove: 'Someone left the group',
  channel_name_change: 'Renamed the group',
  pin: 'Pinned a message',
};

/** One line of plain text from a message: mentions as names, markdown marks and spoilers hidden. */
function plainText(content: string, state: AppState): string {
  return decodeMentions(content, state)
    .replace(/\|\|[\s\S]*?\|\|/g, 'Spoiler')
    .replace(/```\w*/g, '')
    .replace(/^>\s?/gm, '')
    .replace(/[*_~`]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** "You: hi", "Alex: sent a file", "Missed call"... for a conversation row. */
function previewLine(state: AppState, channel: Channel, preview: MessagePreview): string {
  const system = SYSTEM_TEXT[preview.type];
  if (system) return system;
  const mine = preview.authorId === state.me?.id;
  if (!mine && preview.authorId && state.relationships[preview.authorId]?.type === 'blocked') return 'Blocked message';
  const text = plainText(preview.content, state) || (preview.attachments ? (preview.attachments === 1 ? 'Sent a file' : `Sent ${preview.attachments} files`) : '');
  if (mine) return `You: ${text}`;
  const author = preview.authorId ? state.users[preview.authorId] : null;
  return channel.type === 'group_dm' && author ? `${author.displayName}: ${text}` : text;
}

/** A conversation in the phone layout: big avatar, name, newest message and how long ago. */
export function MobileDmRow({ channel, active, onOpen }: { channel: Channel; active: boolean; onOpen: () => void }) {
  const state = useStore.getState();
  const presences = useStore((s) => s.presences);
  const inCall = useStore((s) => Object.keys(s.voiceStates[channel.id] ?? {}).length > 0);
  const partnerInVoice = useStore((s) => {
    const p = channel.type === 'dm' ? dmPartner(s, channel) : null;
    return !!p && Object.values(s.voiceStates).some((m) => !!m[p.id]);
  });
  const now = useNow(60_000);
  const partner = channel.type === 'dm' ? dmPartner(state, channel) : null;
  const name = privateChannelName(state, channel);
  const unread = isUnread(state, channel);
  const mentions = mentionCount(state, channel.id);
  const line = inCall
    ? null
    : channel.lastMessage
      ? previewLine(state, channel, channel.lastMessage)
      : channel.type === 'group_dm'
        ? `${channel.recipients?.length ?? 0} members`
        : '';
  const when = channel.lastMessageId ? shortAgo(idTimestamp(channel.lastMessageId), now) : null;

  return (
    <div
      role="link"
      tabIndex={0}
      aria-label={`${name}${unread ? ', unread' : ''}${mentions ? `, ${mentions} mentions` : ''}`}
      aria-current={active ? 'page' : undefined}
      onClick={onOpen}
      onKeyDown={(e) => e.key === 'Enter' && onOpen()}
      className={clsx('flex cursor-pointer items-center gap-3 rounded-2xl px-2 py-2.5', active ? 'bg-active' : 'active:bg-hover')}
    >
      {partner ? (
        <Avatar user={partner} size={48} status={presences[partner.id] ?? 'offline'} />
      ) : (
        <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent">
          <Users className="size-6" aria-hidden />
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className={clsx('min-w-0 truncate text-base', unread ? 'font-bold text-fg' : 'font-medium text-fg')}>
            <NameText font={partner?.nameFont}>{name}</NameText>
            {partner?.system && <SystemTag />}
          </span>
          {partnerInVoice && !inCall && <Volume2 className="size-4 shrink-0 text-success" aria-label="In voice" />}
          {when && <span className="ml-auto shrink-0 text-xs text-faint">{when}</span>}
        </span>
        <span className="mt-0.5 flex items-center gap-2">
          {inCall ? (
            <span className="flex min-w-0 items-center gap-1.5 truncate text-sm font-medium text-success">
              <Phone className="size-3.5 shrink-0" aria-hidden /> Call in progress
            </span>
          ) : (
            <span className={clsx('min-w-0 flex-1 truncate text-sm', unread ? 'text-fg' : 'text-muted')}>{line}</span>
          )}
          <Badge count={mentions} className="ml-auto shrink-0" />
        </span>
      </span>
    </div>
  );
}
